import {useEffect, useRef, useState} from "react";
import Plot from "react-plotly.js";
import {notification, Radio} from "antd";
import {Resizable} from 'react-resizable';
import 'react-resizable/css/styles.css'
import html2canvas from "html2canvas";
import ResultText from "../ResultText";
import EmptyLine from "../EmptyLine";

const camera = {
    width: 1000,
    height: 1000,
    path: 'm500 450c-83 0-150-67-150-150 0-83 67-150 150-150 83 0 150 67 150 150 0 83-67 150-150 150z m400 150h-120c-16 0-34 13-39 29l-31 93c-6 15-23 28-40 28h-340c-16 0-34-13-39-28l-31-94c-6-15-23-28-40-28h-120c-55 0-100-45-100-100v-450c0-55 45-100 100-100h800c55 0 100 45 100 100v450c0 55-45 100-100 100z m-400-550c-138 0-250 112-250 250 0 138 112 250 250 250 138 0 250-112 250-250 0-138-112-250-250-250z m365 380c-19 0-35 16-35 35 0 19 16 35 35 35 19 0 35-16 35-35 0-19-16-35-35-35z',
    transform: 'matrix(1 0 0 -1 0 850)'
}

const undo = {
    width: 857.1,
    height: 1000,
    path: 'm857 350q0-87-34-166t-91-137-137-92-166-34q-96 0-183 41t-147 114q-4 6-4 13t5 11l76 77q6 5 14 5 9-1 13-7 41-53 100-82t126-29q58 0 110 23t92 61 61 91 22 111-22 111-61 91-92 61-110 23q-55 0-105-20t-90-57l77-77q17-16 8-38-10-23-33-23h-250q-15 0-25 11t-11 25v250q0 24 22 33 22 10 39-8l72-72q60 57 137 88t159 31q87 0 166-34t137-92 91-137 34-166z',
    transform: 'matrix(1 0 0 -1 0 850)'
};

const wsel_plot = {
    type: 'scatter',
    mode: 'markers+lines',
    marker: {
        color: 'blue',
        symbol: 'arrow-down'
    },
    line: {
        width: 0.7
    },
}

const sta_elev_plot = {
    type: 'scatter',
    mode: 'lines+markers',
    marker: {
        color: 'black',
        size: 4,
        symbol: 'square'
    },
    line: {
        width: 0.7
    },
}

const weir_sta_elev_plot = {
    type: 'scatter',
    mode: 'lines+markers',
    marker: {
        color: 'black',
        size: 4,
        symbol: 'square'
    },
    line: {
        width: 0.7,
    },
    fill: 'tonexty',
    fillcolor: '#d0d0d0',
}

const high_chord_plot = {
    type: 'scatter',
    mode: 'lines+markers',
    marker: {
        color: 'red',
        size: 0.5,
        symbol: 'square'
    },
    line: {
        width: 0.7
    },
}

const low_chord_plot = {
    type: 'scatter',
    mode: 'lines+markers',
    marker: {
        color: 'green',
        size: 0.5,
        symbol: 'square'
    },
    line: {
        width: 0.7
    },
}

const obst_plot = {
    type: 'scatter',
    mode: 'markers+lines',
    marker: {
        color: 'black',
        size: 0.5,
        symbol: 'square'
    },
    line: {
        width: 0.7
    },
    fill: 'tonexty',
    fillcolor: '#000000',
}

const ineff_hatch = {
    type: 'scatter',
    mode: 'markers+lines',
    showlegend: false,
    marker: {
        color: 'green',
        size: 0.1,
        symbol: 'square'
    },
    line: {
        width: 0.7
    },
    fill: 'tonexty',
    fillcolor: 'green',
}

const encroachment_plot = {
    type: 'scatter',
    mode: 'markers+lines',
    marker: {color: 'black', symbol: 'square', size: 2},
    line: {
        width: 2,
    },
}

const ineff_plot = {
    type: 'scatter',
    mode: 'markers+lines',
    marker: {color: 'green', symbol: 'square', size: 2},
    line: {
        width: 0.7
    },
}

// Helper function to separate ineff segments into vertical and horizontal
const separateIneffSegments = (ineffData) => {
    if (!ineffData?.x || !ineffData?.y || ineffData.x.length < 2) {
        return { vertical: { x: [], y: [] }, horizontal: { x: [], y: [] } }
    }

    const vertical = { x: [], y: [] }
    const horizontal = { x: [], y: [] }

    for (let i = 0; i < ineffData.x.length - 1; i++) {
        const x1 = ineffData.x[i]
        const y1 = ineffData.y[i]
        const x2 = ineffData.x[i + 1]
        const y2 = ineffData.y[i + 1]

        // Check if segment is vertical (same x) or horizontal (same y)
        if (Math.abs(x1 - x2) < 0.001) {
            // Vertical segment
            if (vertical.x.length > 0) {
                vertical.x.push(null) // Break line
                vertical.y.push(null)
            }
            vertical.x.push(x1, x2)
            vertical.y.push(y1, y2)
        } else if (Math.abs(y1 - y2) < 0.001) {
            // Horizontal segment
            if (horizontal.x.length > 0) {
                horizontal.x.push(null) // Break line
                horizontal.y.push(null)
            }
            horizontal.x.push(x1, x2)
            horizontal.y.push(y1, y2)
        }
    }

    return { vertical, horizontal }
}

const manns_plot = {
    type: 'scatter',
    mode: 'markers+lines',
    marker: {color: 'black', symbol: 'line-ns-open'},
    line: {
        width: 0.7
    },
}

const pier_plot = {
    type: 'scatter',
    mode: 'markers',
    marker: {color: 'black', symbol: 'square', size: 0.3},
    fill: 'toself',
    fillcolor: '#cfcfcf',
    showlegend: false,
}

const abut_plot = {
    type: 'scatter',
    mode: 'markers+lines',
    marker: {color: 'brown', symbol: 'square', size: 0.3},
    line: {color: 'brown', width: 0.5},
    fill: 'toself',
    fillcolor: '#cfcfcf',
    showlegend: false,
}

const manns_text_plot = {
    type: 'scatter',
    mode: 'text',
    marker: {color: 'black', symbol: 'sqaure', size: 0.1},
    showlegend: false,
}

const bank_plot = {
    type: 'scatter',
    mode: 'markers',
    marker: {color: 'red'},
}

const thalweg_plot = {
    type: 'scatter',
    mode: 'markers',
    marker: {color: '#48dbfb'},
}

const low_deck_plot = {
    type: 'scatter',
    mode: 'markers',
    marker: {color: '#2c3e50', symbol: 'circle-x-open'},
}

const high_deck_plot = {
    type: 'scatter',
    mode: 'markers',
    marker: {color: '#2c3e50', symbol: 'square-x-open'},
}

const WSEL_ICONS = {
    FW: 'arrow-down-open',
    YR2: 'diamond-wide',
    YR10: 'diamond-tall',
    YR25: 'arrow-down',
    YR50: 'circle',
    YR100: 'arrow-down',
    YR500: 'square',
    FUT10: 'arrow-down',
    FUT20: 'arrow-down',
    FUT35: 'arrow-down',
    FUTULT: 'arrow-down',
}

const StaElevLayerNames = {
    B_XS_INEFF: 'B: Ineff',
    B_BLOCK_OBST: 'B: Blocked Obst',
    B_ENCR: 'B: Encroachment',
    B_STA_ELEV: 'B: Ground',
    B_STA_ELEV_ORIGINAL: 'B: Ground (Unadj.)',
    B_STA_ELEV_BURN_OUT: 'B: Ground (Replaced)',
    B_STA_ELEV_MODIFIED: 'B: Ground (Surveyed)',
    B_LOW_CHORDS: 'B: Low Chord',
    B_HIGH_CHORDS: 'B: High Chord',
    B_LOW_LOW_CHORDS: 'B: Low. Low Chord',
    B_LOW_HIGH_CHORDS: 'B: Low. High Chord',
    B_PIERS: 'B: Piers',
    B_ABUTMENTS: 'B: Abutments',
    B_WSEL_FW: 'B: WSEL FW',
    B_WSEL_YR2: 'B: WSEL YR2',
    B_WSEL_YR10: 'B: WSEL YR10',
    B_WSEL_YR25: 'B: WSEL YR25',
    B_WSEL_YR50: 'B: WSEL YR50',
    B_WSEL_YR100: 'B: WSEL YR100',
    B_WSEL_YR500: 'B: WSEL YR500',
    B_WSEL_FUT10: 'B: WSEL FUT10',
    B_WSEL_FUT20: 'B: WSEL FUT20',
    B_WSEL_FUT35: 'B: WSEL FUT35',
    B_WSEL_FUTULT: 'B: WSEL FUTULT',
    B_BANKS: 'B: Bank Sta',
    B_BANKS_ORIGINAL: 'B: Bank Sta (Unadj.)',
    B_THALWEG: 'B: Thalweg',
    B_MANN: 'B: Manning\'s n',
    B_WEIR_STA_ELEV: 'B: Weir Station Elevation',
    C_XS_INEFF: 'C: Ineff',
    C_BLOCK_OBST: 'C: Blocked Obst',
    C_ENCR: 'C: Encroachment',
    C_STA_ELEV: 'C: Ground',
    C_STA_ELEV_ORIGINAL: 'C: Ground (Unadj.)',
    C_LOW_CHORDS: 'C: Low Chord',
    C_HIGH_CHORDS: 'C: High Chord',
    C_LOW_LOW_CHORDS: 'C: Low. Low Chord',
    C_LOW_HIGH_CHORDS: 'C: Low. High Chord',
    C_PIERS: 'C: PIERS',
    C_ABUTMENTS: 'C: ABUTMENTS',
    C_WSEL_FW: 'C: WSEL FW',
    C_WSEL_YR2: 'C: WSEL YR2',
    C_WSEL_YR10: 'C: WSEL YR10',
    C_WSEL_YR25: 'C: WSEL YR25',
    C_WSEL_YR50: 'C: WSEL YR50',
    C_WSEL_YR100: 'C: WSEL YR100',
    C_WSEL_YR500: 'C: WSEL YR500',
    C_WSEL_FUT10: 'C: WSEL FUT10',
    C_WSEL_FUT20: 'C: WSEL FUT20',
    C_WSEL_FUT35: 'C: WSEL FUT35',
    C_WSEL_FUTULT: 'C: WSEL FUTULT',
    C_BANKS: 'C: Bank Sta',
    C_BANKS_ORIGINAL: 'C: Bank Sta (Unadj.)',
    C_THALWEG: 'C: Thalweg',
    C_MANN: 'C: Manning\'s n',
    C_WEIR_STA_ELEV: 'C: Weir Station Elevation',
}

const StructureLayouts = {
    ...StaElevLayerNames,
}

const getLayers = (type) => {

    const layers = []
    Object.keys(StaElevLayerNames).forEach(x => {
        if(['C_LOW_CHORDS', 'C_HIGH_CHORDS', 'B_LOW_CHORDS', 'B_HIGH_CHORDS'].includes(x))
            return;

        if(!type)
        {
            layers.push(StaElevLayerNames[x])
            return
        }

        if(StaElevLayerNames[x].includes(`${type}:`)) {
            layers.push(StaElevLayerNames[x])
        }
    })

    return layers
}

const getStructureLayouts = (type) => {

    const layers = []
    Object.keys(StructureLayouts).forEach(x => {
        if(!type)
        {
            layers.push(StructureLayouts[x])
            return
        }

        if(StructureLayouts[x].includes(`${type}:`)) {
            layers.push(StructureLayouts[x])
        }
    })

    return layers
}

const getLayersFromData = (type, plotData) => {
    const layers = []

    if(plotData?.sta_elev?.x?.length > 0) {
        layers.push(`${type}: Ground`)
    }
    if(plotData?.sta_elev_original?.x?.length > 0) {
        layers.push(`${type}: Ground (Unadj.)`)
    }
    if(plotData?.banks?.x?.length > 0) {
        layers.push(`${type}: Bank Sta`)
    }
    if(plotData?.banks_original?.left || plotData?.banks_original?.right) {
        layers.push(`${type}: Bank Sta (Unadj.)`)
    }
    if(plotData?.high_chords?.x?.length > 0) {
        layers.push(`${type}: High Chord`)
    }
    if(plotData?.low_chords?.x?.length > 0) {
        layers.push(`${type}: Low Chord`)
    }
    if(plotData?.manns?.x?.length > 0) {
        layers.push(`${type}: Manning\'s n`)
    }
    if(plotData?.ineffs?.x?.length > 0) {
        layers.push(`${type}: Ineff`)
    }
    if(plotData?.obstruction?.x?.length > 0) {
        layers.push(`${type}: Blocked Obst`)
    }
    if(plotData?.thalweg?.x?.length > 0) {
        layers.push(`${type}: Thalweg`)
    }
    if(plotData?.encroachment?.x?.length > 0) {
        layers.push(`${type}: Encroachment`)
    }
    if(plotData?.low_high_chord?.x?.length > 0) {
        layers.push(`${type}: Low. High Chord`)
    }

    if(plotData?.low_high_chord?.x?.length > 0) {
        layers.push(`${type}: Low. Low Chord`)
    }

    if(plotData?.weir_sta_elev?.x?.length > 0) {
        layers.push(`${type}: Weir Station Elevation`)
    }

    plotData?.wsel?.forEach(w => {
        if(w?.x?.length > 0) {
            layers.push(`${type}: WSEL ${w.profile}`)
        }
    })

    plotData?.abutments?.forEach(w => {
        if(w?.x?.length > 0) {
            layers.push(`${type}: Piers`)
        }
    })

    plotData?.piers?.forEach(w => {
        if(w?.x?.length > 0) {
            layers.push(`${type}: Abutments`)
        }
    })

    return Array.from(new Set(layers)).sort()
}

export const StaElevPlotLayer = {
    getLayersFromData,
    StaElevLayerNames,
    StructureLayouts,
    getLayers,
    getStructureLayouts
}

const getTitleWidth = (title) => {
    if(title.length > 21 && title.length <= 33)
        return 1.10
    if(title.length > 13 && title.length <= 21)
        return 1.10
    if(title.length > 9 && title.length <= 13)
        return 1.08
    if(title.length <= 9)
        return 1.08
}

export default function StationElevationPlot({plotData, globalVariables, crossSection, layers, col=1, CGroundColor, locWselEnable, plotTitle, height, width}) {
    const [radio, setRadio] = useState('Both')
    const userConfig = useRef({})
    const [plot, setPlot] = useState({
        data: [],
        layout: {},
        xax     : {},
        yaxis: {},
        footnotes: []
    })
    const [reset, setReset] = useState(0)
    const [layoutSize, setLayoutSize] = useState({
        width: width || (window.innerWidth-(col === 1 ? 50 : 60))/col,
        height: height || 518,
    })
    const plotImageRef = useRef();
    const plotRef = useRef()

    const createPlotData = ({plotData, crossSection}) => {
        let data = []

        const x_range = [...(plotData?.cmp?.sta_elev?.x || []), ...(plotData?.base?.sta_elev?.x || [])]
        const y_range = [
            ...(plotData?.cmp?.sta_elev?.y || []),
            ...(plotData?.cmp?.manns?.y || []),
            ...(plotData?.base?.manns_text?.y || []),
            ...(plotData?.base?.sta_elev?.y || []),
            ...(plotData?.base?.manns?.y || []),
            ...(plotData?.base?.manns_text?.y || []),
        ]
        let x_min = Math.min(...x_range)
        let x_max = Math.max(...x_range)
        let y_min = Math.min(...y_range)
        let y_max = Math.max(...y_range)

        let limitTrace = {
            showlegend: false,
            x: [x_min, x_max],
            y: [y_min, y_min],
            marker: {color: '#ffffff00'}
        }

        x_min = x_min - (x_min*0.01)
        x_max = x_max + (x_max*0.005)
        y_min = y_min - (y_min*0.01)
        y_max = y_max + (y_max*0.025)

        // Ground Fill Polygon helper values
        const groundFillTolerance = 5
        const y_fill_bottom = Math.min(...y_range) - groundFillTolerance

        if(plotData?.cmp?.manns && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.C_MANN)) {
            data.push({
                ...manns_text_plot,
                x: plotData?.cmp?.manns_text?.x,
                y: plotData?.cmp?.manns_text?.y,
                text: plotData?.cmp?.manns_text?.texts,
                name: 'C: MANN TEXTS',
            })

            data.push({
                ...manns_plot,
                x: plotData?.cmp?.manns.x,
                y: plotData?.cmp?.manns.y,
                texts: plotData?.cmp?.manns?.text || [],
                name: StaElevPlotLayer.StaElevLayerNames.C_MANN,
            })
        }
        if(plotData?.base?.manns && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_MANN)) {
            data.push({
                ...manns_text_plot,
                x: plotData?.base?.manns_text?.x,
                y: plotData?.base?.manns_text?.y,
                text: plotData?.base?.manns_text?.texts,
                name: 'B: MANN_TEXTS'
            })

            data.push({
                ...manns_plot,
                x: plotData?.base?.manns.x,
                y: plotData?.base?.manns.y,
                texts: plotData?.base?.manns?.text || [],
                name: StaElevPlotLayer.StaElevLayerNames.B_MANN,
            })
        }

        // --- Comparison (CMP) structure layers first, then Base (BASE) layers,
        //     so that Base always renders on top of Comparison in Plotly's z-order.

        // CMP: High Chord
        if(plotData?.cmp?.high_chords && layers?.find(x => x === StaElevPlotLayer.StructureLayouts.C_HIGH_CHORDS)) {
            let limitTraceHighChord = {
                showlegend: false,
                x: [Math.min(...plotData?.cmp?.high_chords.x), Math.max(...plotData?.cmp?.high_chords.x)],
                y: [Math.min(...plotData?.base?.sta_elev.y), Math.min(...plotData?.base?.sta_elev.y)],
                marker: {color: '#ffffff00'}
            }

            data.push({
                ...limitTraceHighChord,
            })

            data.push({
                ...high_chord_plot,
                x: plotData?.cmp?.high_chords.x,
                y: plotData?.cmp?.high_chords.y,
                fill: 'tonexty',
                fillcolor: '#cfcfcf',
                customTexts: plotData?.cmp?.high_chords?.texts,
                name: StaElevPlotLayer.StructureLayouts.C_HIGH_CHORDS,
            })
        }

        // CMP: Low Chord
        if(plotData?.cmp?.low_chords && layers?.find(x => x === StaElevPlotLayer.StructureLayouts.C_LOW_CHORDS)) {
            data.push({
                ...limitTrace,
                y: [limitTrace.y[0]-3,limitTrace.y[1]]
            })
            data.push({
                ...low_chord_plot,
                x: plotData?.cmp?.low_chords.x,
                y: plotData?.cmp?.low_chords.y,
                fill: 'tonexty',
                fillcolor: 'white',
                customTexts: plotData?.cmp?.low_chords?.texts,
                name: StaElevPlotLayer.StructureLayouts.C_LOW_CHORDS,
            })
        }

        // CMP: Piers
        if(plotData?.cmp?.piers?.length > 0) {
            data.push({
                ...limitTrace,
                y: [limitTrace.y[0]-3,limitTrace.y[1]]
            })
            plotData?.cmp?.piers?.forEach((pier, idx) => {
                data.push({
                    ...pier_plot,
                    x: pier?.x,
                    y: pier?.y,
                    name: 'C: Piers',
                    showlegend: idx === 0
                })
            })
        }

        // CMP: Abutments
        if(plotData?.cmp?.abutments?.length > 0) {
            data.push({
                ...limitTrace,
                y: [limitTrace.y[0]-3,limitTrace.y[1]]
            })
            plotData?.cmp?.abutments?.forEach((abut, idx) => {
                data.push({
                    ...abut_plot,
                    x: abut?.x,
                    y: abut?.y,
                    name: 'C: Abutments',
                    showlegend: idx === 0
                })
            })
        }

        // CMP: HATCHING - drawn before Ground Fill so it appears underneath
        if(plotData?.cmp?.ineffs && layers?.find(x => x === "C: Ineff")) {
            if(plotData?.cmp?.wsel && plotData?.cmp?.wsel.length > 0) {
                plotData?.cmp?.hatches?.forEach(hatching => {
                    if(layers?.find(y => y === 'C: WSEL ' + hatching.profile)) {
                        if(hatching?.data?.length > 0) {
                            hatching?.data?.forEach(h => {
                                const limit = JSON.parse(JSON.stringify(limitTrace))
                                limit.x = [Math.min(...h.x), Math.max(...h.x)]
                                data.push(limit)
                                data.push({
                                    ...ineff_hatch,
                                    ...h,
                                    name: `C: XS HATCHING ${hatching.profile}`
                                })
                            })
                        }
                    }
                })
            }
        }

        // CMP: Ineff vertical segments - drawn before Ground Fill so they appear underneath
        if(plotData?.cmp?.ineffs && layers?.find(x => x === "C: Ineff")) {
            const cmpIneffSegments = separateIneffSegments(plotData.cmp.ineffs)
            if (cmpIneffSegments.vertical.x.length > 0) {
                data.push({
                    ...ineff_plot,
                    mode: 'lines',
                    x: cmpIneffSegments.vertical.x,
                    y: cmpIneffSegments.vertical.y,
                    showlegend: false,
                    name: 'C: Ineff Vertical',
                    legendgroup: 'Comparison',
                    hoverinfo: 'skip'
                })
            }
        }

        // CMP: Ground Fill Polygon - drawn after structures, before culverts
        // Creates closed shape: ground points on top, rectangle closing at bottom
        if(plotData?.cmp?.sta_elev && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV)) {
            const cmpStaElev = plotData.cmp.sta_elev
            const fillX = [...cmpStaElev.x, cmpStaElev.x[cmpStaElev.x.length - 1], cmpStaElev.x[0], cmpStaElev.x[0]]
            const fillY = [...cmpStaElev.y, y_fill_bottom, y_fill_bottom, cmpStaElev.y[0]]
            data.push({
                type: 'scatter',
                mode: 'lines',
                x: fillX,
                y: fillY,
                fill: 'toself',
                fillcolor: '#ffffff',
                line: { color: 'rgba(0,0,0,0)', width: 0 },
                showlegend: false,
                name: 'C: Ground Fill',
                legendgroup: 'Comparison',
                hoverinfo: 'skip'
            })
        }

        // CMP: Culverts
        if(plotData?.cmp?.culverts?.length > 0) {
            plotData?.cmp?.culverts.forEach((c, culvertIdx) => {
                const culvertName = plotData?.cmp?.culverts?.length > 1 
                    ? `C: Culvert #${culvertIdx + 1}` 
                    : 'C: Culvert'
                c?.shapes?.forEach((s, idx) => {
                    data.push({
                        x: s.x,
                        y: s.y,
                        fill: 'toself',
                        fillcolor: 'white',
                        line: {color: 'black', width: 0.5},
                        marker: {color: 'black', size: 0.5},
                        showlegend: idx === 0,
                        name: culvertName
                    })
                })
            })
        }

        // BASE: High Chord
        if(plotData?.base?.high_chords && layers?.find(x => x === StaElevPlotLayer.StructureLayouts.B_HIGH_CHORDS)) {
            let limitTraceHighChord = {
                showlegend: false,
                x: [Math.min(...plotData?.base?.high_chords.x), Math.max(...plotData?.base?.high_chords.x)],
                y: [Math.min(...plotData?.base?.sta_elev.y), Math.min(...plotData?.base?.sta_elev.y)],
                marker: {color: '#ffffff00'}
            }

            data.push({
                ...limitTraceHighChord
            })

            const trace = {
                ...high_chord_plot,
                x: plotData?.base?.high_chords.x,
                y: plotData?.base?.high_chords.y,
                customTexts: plotData?.base?.high_chords?.texts,
                name: StaElevPlotLayer.StructureLayouts.B_HIGH_CHORDS,
                fill: 'tonexty',
                fillcolor: '#cfcfcf',
            }

            data.push(trace)
        }

        // BASE: Low Chord
        if(plotData?.base?.low_chords && layers?.find(x => x === StaElevPlotLayer.StructureLayouts.B_LOW_CHORDS)) {
            data.push({
                ...limitTrace,
                y: [limitTrace.y[0]-3,limitTrace.y[1]]
            })
            const trace = {
                ...low_chord_plot,
                x: plotData?.base?.low_chords.x,
                y: plotData?.base?.low_chords.y,
                customTexts: plotData?.base?.low_chords?.texts,
                name: StaElevPlotLayer.StructureLayouts.B_LOW_CHORDS,
                fill: 'tonexty',
                fillcolor: '#ffffff',
            }

            data.push(trace)
        }

        // BASE: Piers
        if(plotData?.base?.piers?.length > 0) {
            data.push({
                ...limitTrace,
                y: [limitTrace.y[0]-3,limitTrace.y[1]]
            })
            plotData?.base?.piers?.forEach((pier, idx) => {
                data.push({
                    ...pier_plot,
                    x: pier?.x,
                    y: pier?.y,
                    name: 'B: Piers',
                    showlegend: idx === 0
                })
            })
        }

        // BASE: Abutments
        if(plotData?.base?.abutments?.length > 0) {
            data.push({
                ...limitTrace,
                y: [limitTrace.y[0]-3,limitTrace.y[1]]
            })
            plotData?.base?.abutments?.forEach((abut, idx) => {
                data.push({
                    ...abut_plot,
                    x: abut?.x,
                    y: abut?.y,
                    name: 'B: Abutments',
                    showlegend: idx === 0
                })
            })
        }

        // BASE: HATCHING - drawn before Ground Fill so it appears underneath
        if(plotData?.base?.ineffs && layers?.find(x => x === "B: Ineff")) {
            if(plotData?.base?.wsel && plotData?.base?.wsel.length > 0) {
                plotData?.base?.hatches?.forEach(hatching => {
                    if(layers?.find(y => y === 'B: WSEL ' + hatching.profile)) {
                        if(hatching?.data?.length > 0) {
                            hatching?.data?.forEach(h => {
                                const limit = JSON.parse(JSON.stringify(limitTrace))
                                limit.x = [Math.min(...h.x), Math.max(...h.x)]
                                data.push(limit)
                                data.push({
                                    ...ineff_hatch,
                                    ...h,
                                    name: `B: XS HATCHING ${hatching.profile}`
                                })
                            })
                        }
                    }
                })
            }
        }

        // BASE: Ineff vertical segments - drawn before Ground Fill so they appear underneath
        if(plotData?.base?.ineffs && layers?.find(x => x === "B: Ineff")) {
            const baseIneffSegments = separateIneffSegments(plotData.base.ineffs)
            if (baseIneffSegments.vertical.x.length > 0) {
                data.push({
                    ...ineff_plot,
                    mode: 'lines',
                    x: baseIneffSegments.vertical.x,
                    y: baseIneffSegments.vertical.y,
                    showlegend: false,
                    name: 'B: Ineff Vertical',
                    legendgroup: 'Base',
                    hoverinfo: 'skip'
                })
            }
        }

        // BASE: Ground Fill Polygon - drawn after structures, before culverts
        // Creates closed shape: ground points on top, rectangle closing at bottom
        if(plotData?.base?.sta_elev && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV)) {
            const baseStaElev = plotData.base.sta_elev
            const fillX = [...baseStaElev.x, baseStaElev.x[baseStaElev.x.length - 1], baseStaElev.x[0], baseStaElev.x[0]]
            const fillY = [...baseStaElev.y, y_fill_bottom, y_fill_bottom, baseStaElev.y[0]]
            data.push({
                type: 'scatter',
                mode: 'lines',
                x: fillX,
                y: fillY,
                fill: 'toself',
                fillcolor: '#ffffff',
                line: { color: 'rgba(0,0,0,0)', width: 0 },
                showlegend: false,
                name: 'B: Ground Fill',
                legendgroup: 'Base',
                hoverinfo: 'skip'
            })
        }

        // BASE: Culverts
        if(plotData?.base?.culverts?.length > 0) {
            plotData?.base?.culverts.forEach((c, culvertIdx) => {
                const culvertName = plotData?.base?.culverts?.length > 1 
                    ? `B: Culvert #${culvertIdx + 1}` 
                    : 'B: Culvert'
                c?.shapes?.forEach((s, idx) => {
                    data.push({
                        x: s.x,
                        y: s.y,
                        fill: 'toself',
                        fillcolor: 'white',
                        line: {color: 'black', width: 0.5},
                        marker: {color: 'black', size: 0.5},
                        showlegend: idx === 0,
                        name: culvertName
                    })
                })
            })
        }


        // CMP: Ineff horizontal segments - drawn after Ground Fill so they appear on top
        if(plotData?.cmp?.ineffs && layers?.find(x => x === `C: Ineff`)) {
            const cmpIneffSegments = separateIneffSegments(plotData.cmp.ineffs)
            data.push({
                ...ineff_plot,
                x: cmpIneffSegments.horizontal.x,
                y: cmpIneffSegments.horizontal.y,
                name: StaElevPlotLayer.StaElevLayerNames.C_XS_INEFF,
            })
        }
        // BASE: Ineff horizontal segments - drawn after Ground Fill so they appear on top
        if(plotData?.base?.ineffs && layers?.find(x => x === "B: Ineff")) {
            const baseIneffSegments = separateIneffSegments(plotData.base.ineffs)
            data.push({
                ...ineff_plot,
                x: baseIneffSegments.horizontal.x,
                y: baseIneffSegments.horizontal.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_XS_INEFF,
            })
        }

        if(plotData?.cmp?.obstruction && layers?.find(x => x === "C: Blocked Obst")) {
            data.push(limitTrace)
            data.push({
                ...obst_plot,
                x: plotData?.cmp?.obstruction.x,
                y: plotData?.cmp?.obstruction.y,
                name: StaElevPlotLayer.StaElevLayerNames.C_BLOCK_OBST,
            })
        }
        if(plotData?.base?.obstruction && layers?.find(x => x === "B: Blocked Obst")) {
            data.push(limitTrace)
            data.push({
                ...obst_plot,
                x: plotData?.base?.obstruction.x,
                y: plotData?.base?.obstruction.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_BLOCK_OBST,
            })
        }

        if(plotData?.cmp?.weir_sta_elev && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.C_WEIR_STA_ELEV)) {
            data.push(limitTrace)
            data.push({
                ...weir_sta_elev_plot,
                x: plotData?.cmp?.weir_sta_elev.x,
                y: plotData?.cmp?.weir_sta_elev.y,
                name: StaElevPlotLayer.StaElevLayerNames.C_WEIR_STA_ELEV,
            })
        }
        if(plotData?.base?.weir_sta_elev && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_WEIR_STA_ELEV)) {
            data.push(limitTrace)
            data.push({
                ...weir_sta_elev_plot,
                x: plotData?.base?.weir_sta_elev.x,
                y: plotData?.base?.weir_sta_elev.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_WEIR_STA_ELEV,
            })
        }

        if(plotData?.cmp?.encroachment && layers?.find(x => x === "C: Encroachment")) {
            data.push({
                ...encroachment_plot,
                x: plotData?.cmp?.encroachment.x,
                y: plotData?.cmp?.encroachment.y,
                name: StaElevPlotLayer.StaElevLayerNames.C_ENCR,
            })
        }
        if(plotData?.base?.encroachment && layers?.find(x => x === "B: Encroachment")) {
            data.push({
                ...encroachment_plot,
                x: plotData?.base?.encroachment.x,
                y: plotData?.base?.encroachment.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_ENCR,
            })
        }

        if(plotData?.base?.wsel && plotData?.base?.wsel.length > 0) {
            plotData?.base?.wsel?.sort((a, b) => a.profile < b.profile ? -1 : 1).reverse().forEach(x => {
                if(layers?.find(y => y === 'B: WSEL ' + x.profile)) {
                    const _wsel = JSON.parse(JSON.stringify(wsel_plot))
                    data.push({
                        ..._wsel,
                        x: x.x,
                        y: x.y,
                        name: 'B: WSEL ' + x.profile,
                        marker: {
                            ..._wsel.marker,
                            symbol: WSEL_ICONS[x.profile],
                            size: locWselEnable ? 4 : 7
                        },
                    })
                }
            })
        }
        if(plotData?.cmp?.wsel && plotData?.cmp?.wsel.length > 0) {
            plotData?.cmp?.wsel?.sort((a, b) => a.profile < b.profile ? -1 : 1).reverse().forEach(x => {
                if(layers?.find(y => y === 'C: WSEL ' + x.profile)) {
                    const _wsel = JSON.parse(JSON.stringify(wsel_plot))
                    data.push({
                        ..._wsel,
                        x: x.x,
                        y: x.y,
                        name: 'C: WSEL ' + x.profile,
                        marker: {
                            ..._wsel.marker,
                            symbol: WSEL_ICONS[x.profile]
                        },
                    })
                }
            })
        }


        // const white_tolerance = (y_max-y_min)/4
        const white_tolerance = 5
        if(plotData?.cmp?.sta_elev && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV)) {
            // Ground fill polygon is already drawn at the beginning, so we only draw the line here
            const trace = {
                ...sta_elev_plot,
                line: {
                    ...sta_elev_plot.line,
                    color: 'brown'
                },
                marker: {
                    ...sta_elev_plot.marker,
                    color: 'brown'
                },
                x: plotData?.cmp?.sta_elev.x,
                y: plotData?.cmp?.sta_elev.y,
                customTexts: plotData?.cmp?.sta_elev?.texts,
                name: StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV,
            }
            data.push(trace)
        }

        if(plotData?.cmp?.sta_elev_original && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV_ORIGINAL)) {
            const trace = {
                ...sta_elev_plot,
                line: {
                    ...sta_elev_plot.line,
                    color: '#ff6b6b',
                    dash: 'dot'
                },
                marker: {
                    ...sta_elev_plot.marker,
                    color: '#ff6b6b',
                    size: 3
                },
                x: plotData?.cmp?.sta_elev_original.x,
                y: plotData?.cmp?.sta_elev_original.y,
                customTexts: plotData?.cmp?.sta_elev_original?.texts,
                name: StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV_ORIGINAL,
            }
            data.push(trace)
        }

        if(plotData?.base?.sta_elev && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV)) {
            // Ground fill polygon is already drawn at the beginning, so we only draw the line here
            const trace = {
                ...sta_elev_plot,
                line: {
                    ...sta_elev_plot.line,
                    color: '#e1b12c'
                },
                marker: {
                    ...sta_elev_plot.marker,
                    color: '#e1b12c'
                },
                x: plotData?.base?.sta_elev.x,
                y: plotData?.base?.sta_elev.y,
                customTexts: plotData?.base?.sta_elev?.texts,
                name: StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV,
            }
            data.push(trace)
        }

        if(plotData?.base?.sta_elev_original && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV_ORIGINAL)) {
            const trace = {
                ...sta_elev_plot,
                line: {
                    ...sta_elev_plot.line,
                    color: '#95a5a6',
                    dash: 'dot'
                },
                marker: {
                    ...sta_elev_plot.marker,
                    color: '#95a5a6',
                    size: 3
                },
                x: plotData?.base?.sta_elev_original.x,
                y: plotData?.base?.sta_elev_original.y,
                customTexts: plotData?.base?.sta_elev_original?.texts,
                name: StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV_ORIGINAL,
            }
            data.push(trace)
        }

        if(plotData?.base?.sta_elev && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV_BURN_OUT)) {
            const trace = {
                ...sta_elev_plot,
                line: {
                    ...sta_elev_plot.line,
                    color: '#afafaf'
                },
                marker: {
                    ...sta_elev_plot.marker,
                    color: '#afafaf'
                },
                x: plotData?.base?.sta_elev_burn_out.x,
                y: plotData?.base?.sta_elev_burn_out.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV_BURN_OUT,
            }

            data.push(trace)
        }

        if(plotData?.base?.sta_elev && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV_MODIFIED)) {
            const trace = {
                ...sta_elev_plot,
                line: {
                    ...sta_elev_plot.line,
                    color: '#00FF00'
                },
                marker: {
                    ...sta_elev_plot.marker,
                    color: '#00FF00'
                },
                x: plotData?.base?.sta_elev_burn.x,
                y: plotData?.base?.sta_elev_burn.y,
                customTexts: plotData?.base?.sta_elev_burn?.texts,
                name: StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV_MODIFIED,
            }

            data.push(trace)
        }

        if(plotData?.cmp?.banks && layers?.find(x => x === "C: Bank Sta")) {
            data.push({
                ...bank_plot,
                x: plotData?.cmp?.banks.x,
                y: plotData?.cmp?.banks.y,
                name: StaElevPlotLayer.StaElevLayerNames.C_BANKS,
            })
        }
        if(plotData?.base?.banks && layers?.find(x => x === "B: Bank Sta")) {
            data.push({
                ...bank_plot,
                x: plotData?.base?.banks.x,
                y: plotData?.base?.banks.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_BANKS,
            })
        }

        if(plotData?.cmp?.banks_original && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.C_BANKS_ORIGINAL)) {
            data.push({
                ...bank_plot,
                marker: {
                    ...bank_plot.marker,
                    color: '#e74c3c',
                    symbol: 'circle-open'
                },
                x: [plotData?.cmp?.banks_original.left, plotData?.cmp?.banks_original.right],
                y: [
                    plotData?.cmp?.sta_elev_original?.y?.[plotData?.cmp?.sta_elev_original?.x?.findIndex(x => x === plotData?.cmp?.banks_original.left)] || 0,
                    plotData?.cmp?.sta_elev_original?.y?.[plotData?.cmp?.sta_elev_original?.x?.findIndex(x => x === plotData?.cmp?.banks_original.right)] || 0
                ],
                name: StaElevPlotLayer.StaElevLayerNames.C_BANKS_ORIGINAL,
            })
        }
        if(plotData?.base?.banks_original && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_BANKS_ORIGINAL)) {
            data.push({
                ...bank_plot,
                marker: {
                    ...bank_plot.marker,
                    color: '#7f8c8d',
                    symbol: 'circle-open'
                },
                x: [plotData?.base?.banks_original.left, plotData?.base?.banks_original.right],
                y: [
                    plotData?.base?.sta_elev_original?.y?.[plotData?.base?.sta_elev_original?.x?.findIndex(x => x === plotData?.base?.banks_original.left)] || 0,
                    plotData?.base?.sta_elev_original?.y?.[plotData?.base?.sta_elev_original?.x?.findIndex(x => x === plotData?.base?.banks_original.right)] || 0
                ],
                name: StaElevPlotLayer.StaElevLayerNames.B_BANKS_ORIGINAL,
            })
        }

        if(plotData?.cmp?.thalweg && layers?.find(x => x === "C: Thalweg")) {
            data.push({
                ...thalweg_plot,
                x: plotData?.cmp?.thalweg.x,
                y: plotData?.cmp?.thalweg.y,
                name: StaElevPlotLayer.StaElevLayerNames.C_THALWEG,
            })
        }
        if(plotData?.base?.thalweg && layers?.find(x => x === "B: Thalweg")) {
            data.push({
                ...thalweg_plot,
                x: plotData?.base?.thalweg.x,
                y: plotData?.base?.thalweg.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_THALWEG,
            })
        }

        if(plotData?.cmp?.low_high_chord && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.C_LOW_HIGH_CHORDS)) {
            data.push({
                ...high_deck_plot,
                x: plotData?.cmp?.low_high_chord.x,
                y: plotData?.cmp?.low_high_chord.y,
                name: StaElevPlotLayer.StaElevLayerNames.C_LOW_HIGH_CHORDS,
            })
        }
        if(plotData?.base?.low_high_chord && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_LOW_HIGH_CHORDS)) {
            data.push({
                ...high_deck_plot,
                x: plotData?.base?.low_high_chord.x,
                y: plotData?.base?.low_high_chord.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_LOW_HIGH_CHORDS,
            })
        }

        if(plotData?.cmp?.low_low_chord && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.C_LOW_LOW_CHORDS)) {
            data.push({
                ...low_deck_plot,
                x: plotData?.cmp?.low_low_chord.x,
                y: plotData?.cmp?.low_low_chord.y,
                name: StaElevPlotLayer.StaElevLayerNames.C_LOW_LOW_CHORDS,
            })
        }
        if(plotData?.base?.low_low_chord && layers?.find(x => x === StaElevPlotLayer.StaElevLayerNames.B_LOW_LOW_CHORDS)) {
            data.push({
                ...low_deck_plot,
                x: plotData?.base?.low_low_chord.x,
                y: plotData?.base?.low_low_chord.y,
                name: StaElevPlotLayer.StaElevLayerNames.B_LOW_LOW_CHORDS,
            })
        }


        data.forEach(trace => {
            if(trace.name === StaElevPlotLayer.StaElevLayerNames.B_MANN || trace.name === StaElevPlotLayer.StaElevLayerNames.B_MANN) {
                trace.hovertext = trace.x?.map((val, idx) => `<span style="margin-left: 4px;"> ${trace.name}</span> <br> ${val?.toFixed(2)}, ${trace?.texts[idx]}`);
                trace.hoverinfo = 'text';
            }
            else if(trace?.name === 'B: MANN_TEXTS' || trace?.name === 'C: MANN_TEXTS') {
                trace.hovertext = trace.x?.map((val, idx) => ``);
                trace.hoverinfo = 'text';
            }
            else if(trace.name) {
                // trace.hovertext = trace.x?.map((val, idx) => `<span style="margin-left: 4px;"> ${trace.name + (trace.customTexts?.length > 0 ? ` (${trace.customTexts[idx]})` : '')}</span> <br> ${val?.toFixed(2)}, ${trace.y[idx]?.toFixed(2)}`);
                trace.hovertext = trace.x?.map((val, idx) => `<span style="margin-left: 4px;"> ${trace.name + (trace.customTexts?.length > 0 && trace.customTexts[idx] ? ` (${trace.customTexts[idx]})` : '')}</span> <br> ${val?.toFixed(2)}, ${trace.y[idx]?.toFixed(2)}`);
                trace.hoverinfo = 'text';
            }
        })

        let title = `RS ${crossSection} (${globalVariables?.sel_plan_title} vs ${globalVariables?.tar_plan_title})`;
        if(!globalVariables?.tar_plan_title)
            title = `RS ${crossSection} (${globalVariables?.plan_title})`;
        if(plotTitle)
            title = plotTitle

        return {
            data,
            x_min, x_max, y_max, y_min, title
        }
    }

    useEffect(() => {
        if(plotData) {
            const plotData1 = createPlotData({plotData, crossSection})
            plotData1?.data?.forEach((x, idx) => {
                x.originalName = x.name
                x.defaultVisible = x.visible


                if(x.name?.includes('B:'))
                {
                    x.legendgroup = 'Base'
                    x.legendgrouptitle_text = 'Base'
                }

                if(x.name?.includes('C:'))
                {
                    x.legendgroup = 'Comparison'
                    x.legendgrouptitle_text = 'Comparison'
                    x.legend = 'legend2'
                }

                if(x.name) {
                    x.name = x.name.replaceAll(`B: `, ``)
                    x.name = x.name.replaceAll(`C: `, ``)
                }
            })

            setVisibility(radio, plotData1)

            setPlot({
                data: plotData1.data,
                footnotes: [...(plotData?.base?.footnotes || []), ...(plotData?.cmp?.footnotes || [])],
                layout: {
                    title: plotData1.title
                },
                xaxis: {
                    range: [plotData1.x_min, plotData1.x_max],
                    title: {
                        text: 'Station (ft)',
                        font: {
                            family: 'Segoe ui',
                            size: 19
                        },
                        standoff: 15
                    },
                    tickfont: {
                        size: 16,
                        color: 'black'
                    },
                    showgrid: true,
                    zeroline: false,
                    showline: true,
                },
                yaxis: {
                    range: [plotData1.y_min, plotData1.y_max],
                    title: {
                        text: 'Elevation (ft)',
                        font: {
                            family: 'Segoe ui',
                            size: 19
                        },
                        standoff: 15
                    },
                    tickfont: {
                        size: 16,
                        color: 'black'
                    },
                    showgrid: true,
                    zeroline: false,
                    showline: true,
                },
            })
        }
    }, [plotData])

    useEffect(() => {
        const elem = document.getElementById('plot-description-panel')
    }, []);

    const convertBase64ToBlob = (base64Image) => {
        const parts = base64Image.split(';base64,');
        const imageType = parts[0].split(':')[1];
        const decodedData = window.atob(parts[1]);
        const uInt8Array = new Uint8Array(decodedData.length);
        for (let i = 0; i < decodedData.length; ++i) {
            uInt8Array[i] = decodedData.charCodeAt(i);
        }
        return new Blob([uInt8Array], { type: imageType });
    }

    const takeScreenShot = async function takeScreenShot(node) {
        if (!node) {
            throw new Error('You should provide correct html node.');
        }

        const image = await html2canvas(node, {scale: 5}).then(function (canvas) {
            const croppedCanvas = document.createElement('canvas');
            const croppedCanvasContext = croppedCanvas.getContext('2d'); // init data

            const cropPositionTop = 0;
            const cropPositionLeft = 0;
            const cropWidth = canvas.width;
            const cropHeight = canvas.height;
            croppedCanvas.width = cropWidth;
            croppedCanvas.height = cropHeight;
            croppedCanvasContext.drawImage(canvas, cropPositionLeft, cropPositionTop);
            const base64Image = croppedCanvas.toDataURL();
            return base64Image;
        });

        return image;
    };

    const setVisibility = (type, plot) => {
        // Traces that need fill toggling between modes
        // Note: B_STA_ELEV no longer uses fill - we use separate Ground Fill Polygon instead
        const baseFillTraces = [
            StaElevPlotLayer.StructureLayouts.B_HIGH_CHORDS,
            StaElevPlotLayer.StructureLayouts.B_LOW_CHORDS,
        ]
        const baseFillConfig = {
            [StaElevPlotLayer.StructureLayouts.B_HIGH_CHORDS]: { fill: 'tonexty', fillcolor: '#cfcfcf' },
            [StaElevPlotLayer.StructureLayouts.B_LOW_CHORDS]: { fill: 'tonexty', fillcolor: '#ffffff' },
        }

        // Base colors (for Comparison mode - show C traces with Base colors)
        const baseColorConfig = {
            [StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV]: { line: { color: '#e1b12c', width: 0.7 }, marker: { color: '#e1b12c', size: 4, symbol: 'square' } },
            [StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV_ORIGINAL]: { line: { color: '#95a5a6', width: 0.7, dash: 'dot' }, marker: { color: '#95a5a6', size: 3, symbol: 'square' } },
            [StaElevPlotLayer.StaElevLayerNames.C_BANKS]: { marker: { color: 'red' } },
            [StaElevPlotLayer.StaElevLayerNames.C_BANKS_ORIGINAL]: { marker: { color: '#7f8c8d', symbol: 'circle-open' } },
            [StaElevPlotLayer.StaElevLayerNames.C_THALWEG]: { marker: { color: '#48dbfb' } },
            [StaElevPlotLayer.StructureLayouts.C_HIGH_CHORDS]: { line: { color: 'red', width: 0.7 }, marker: { color: 'red', size: 0.5, symbol: 'square' } },
            [StaElevPlotLayer.StructureLayouts.C_LOW_CHORDS]: { line: { color: 'green', width: 0.7 }, marker: { color: 'green', size: 0.5, symbol: 'square' } },
            [StaElevPlotLayer.StaElevLayerNames.C_LOW_HIGH_CHORDS]: { marker: { color: '#2c3e50', symbol: 'square-x-open' } },
            [StaElevPlotLayer.StaElevLayerNames.C_LOW_LOW_CHORDS]: { marker: { color: '#2c3e50', symbol: 'circle-x-open' } },
            [StaElevPlotLayer.StaElevLayerNames.C_MANN]: { line: { color: 'black', width: 0.7 }, marker: { color: 'black', symbol: 'line-ns-open' } },
            [StaElevPlotLayer.StaElevLayerNames.C_XS_INEFF]: { line: { color: 'green', width: 0.7 }, marker: { color: 'green', symbol: 'square', size: 2 } },
            [StaElevPlotLayer.StaElevLayerNames.C_BLOCK_OBST]: { line: { color: 'black', width: 0.7 }, marker: { color: 'black', size: 0.5, symbol: 'square' }, fillcolor: '#000000' },
            [StaElevPlotLayer.StaElevLayerNames.C_ENCR]: { line: { color: 'black', width: 2 }, marker: { color: 'black', symbol: 'square', size: 2 } },
            [StaElevPlotLayer.StaElevLayerNames.C_WEIR_STA_ELEV]: { line: { color: 'black', width: 0.7 }, marker: { color: 'black', size: 4, symbol: 'square' }, fillcolor: '#d0d0d0' },
            'C: Piers': { marker: { color: 'black', symbol: 'square', size: 0.3 }, fillcolor: '#cfcfcf' },
            'C: Abutments': { line: { color: 'brown', width: 0.5 }, marker: { color: 'brown', symbol: 'square', size: 0.3 }, fillcolor: '#cfcfcf' },
            'C: Culvert': { line: { color: 'black', width: 0.5 }, marker: { color: 'black', size: 0.5 }, fillcolor: 'white' },
        }

        // Comparison original colors (for Both mode)
        const comparisonColorConfig = {
            [StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV]: { line: { color: 'brown', width: 0.7 }, marker: { color: 'brown', size: 4, symbol: 'square' } },
            [StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV_ORIGINAL]: { line: { color: '#ff6b6b', width: 0.7, dash: 'dot' }, marker: { color: '#ff6b6b', size: 3, symbol: 'square' } },
            [StaElevPlotLayer.StaElevLayerNames.C_BANKS]: { marker: { color: 'red' } },
            [StaElevPlotLayer.StaElevLayerNames.C_BANKS_ORIGINAL]: { marker: { color: '#e74c3c', symbol: 'circle-open' } },
            [StaElevPlotLayer.StaElevLayerNames.C_THALWEG]: { marker: { color: '#48dbfb' } },
            [StaElevPlotLayer.StructureLayouts.C_HIGH_CHORDS]: { line: { color: 'red', width: 0.7 }, marker: { color: 'red', size: 0.5, symbol: 'square' } },
            [StaElevPlotLayer.StructureLayouts.C_LOW_CHORDS]: { line: { color: 'green', width: 0.7 }, marker: { color: 'green', size: 0.5, symbol: 'square' } },
            [StaElevPlotLayer.StaElevLayerNames.C_LOW_HIGH_CHORDS]: { marker: { color: '#2c3e50', symbol: 'square-x-open' } },
            [StaElevPlotLayer.StaElevLayerNames.C_LOW_LOW_CHORDS]: { marker: { color: '#2c3e50', symbol: 'circle-x-open' } },
            [StaElevPlotLayer.StaElevLayerNames.C_MANN]: { line: { color: 'black', width: 0.7 }, marker: { color: 'black', symbol: 'line-ns-open' } },
            [StaElevPlotLayer.StaElevLayerNames.C_XS_INEFF]: { line: { color: 'green', width: 0.7 }, marker: { color: 'green', symbol: 'square', size: 2 } },
            [StaElevPlotLayer.StaElevLayerNames.C_BLOCK_OBST]: { line: { color: 'black', width: 0.7 }, marker: { color: 'black', size: 0.5, symbol: 'square' }, fillcolor: '#000000' },
            [StaElevPlotLayer.StaElevLayerNames.C_ENCR]: { line: { color: 'black', width: 2 }, marker: { color: 'black', symbol: 'square', size: 2 } },
            [StaElevPlotLayer.StaElevLayerNames.C_WEIR_STA_ELEV]: { line: { color: 'black', width: 0.7 }, marker: { color: 'black', size: 4, symbol: 'square' }, fillcolor: '#d0d0d0' },
            'C: Piers': { marker: { color: 'black', symbol: 'square', size: 0.3 }, fillcolor: '#cfcfcf' },
            'C: Abutments': { line: { color: 'brown', width: 0.5 }, marker: { color: 'brown', symbol: 'square', size: 0.3 }, fillcolor: '#cfcfcf' },
            'C: Culvert': { line: { color: 'black', width: 0.5 }, marker: { color: 'black', size: 0.5 }, fillcolor: 'white' },
        }

        const applyColorConfig = (trace, config) => {
            const cfg = config[trace.originalName]
            if(cfg) {
                if(cfg.line) trace.line = { ...trace.line, ...cfg.line }
                if(cfg.marker) trace.marker = { ...trace.marker, ...cfg.marker }
                if(cfg.fillcolor) trace.fillcolor = cfg.fillcolor
            }
            // Handle WSEL profiles dynamically
            if(trace.originalName?.startsWith('C: WSEL')) {
                trace.line = { ...trace.line, color: 'blue', width: 0.7 }
                trace.marker = { ...trace.marker, color: 'blue' }
            }
        }

        if(type === 'Base') {
            plot?.data?.forEach(x => {
                if(x.legendgroup === 'Comparison') {
                    x.visible = 'legendonly'
                }
                else if(x.legendgroup === 'Base') {
                    x.visible = userConfig.current[x.originalName] || x.defaultVisible
                    // Restore fill for Base traces (as if comparison doesn't exist)
                    if(baseFillTraces.includes(x.originalName)) {
                        x.fill = baseFillConfig[x.originalName].fill
                        x.fillcolor = baseFillConfig[x.originalName].fillcolor
                    }
                    // Restore solid gray fill for piers and abutments
                    if(x.originalName === 'B: Piers') {
                        x.fillcolor = '#cfcfcf'
                        x.line = undefined
                    }
                    if(x.originalName === 'B: Abutments') {
                        x.fillcolor = '#cfcfcf'
                    }
                }
            })
        }
        else if(type === 'Both') {
            // Only suppress Base fills when there is actually comparison data to show
            const hasComparison = plot?.data?.some(x => x.legendgroup === 'Comparison')
            plot?.data?.forEach(x => {
                x.visible = userConfig.current[x.originalName] || x.defaultVisible
                // Remove fill from Base ground/chord traces so Comparison version shows through
                if(baseFillTraces.includes(x.originalName)) {
                    if(hasComparison) {
                        x.fill = undefined
                        x.fillcolor = undefined
                    } else {
                        // Single-plan (check plot): restore fills as in Base mode
                        x.fill = baseFillConfig[x.originalName].fill
                        x.fillcolor = baseFillConfig[x.originalName].fillcolor
                    }
                }
                // In Both mode, Base piers/abutments show as outline-only so the
                // Comparison plan structure beneath them remains visible
                if(hasComparison && x.originalName === 'B: Piers') {
                    x.fillcolor = 'rgba(0,0,0,0)'
                    x.line = { color: 'black', width: 1 }
                }
                if(hasComparison && x.originalName === 'B: Abutments') {
                    x.fillcolor = 'rgba(0,0,0,0)'
                }
                // Restore Comparison original colors
                applyColorConfig(x, comparisonColorConfig)
            })
        }
        else if(type === 'Comparison') {
            plot?.data?.forEach(x => {
                if(x.legendgroup === 'Base') {
                    x.visible = 'legendonly'
                }
                else if(x.legendgroup === 'Comparison') {
                    x.visible = userConfig.current[x.originalName] || x.defaultVisible
                    // Apply Base colors to Comparison traces
                    applyColorConfig(x, baseColorConfig)
                }
            })
        }
    }

    useEffect(() => {
        setVisibility(radio, plot)
    }, [radio]);

    const handleClick = (data) => {
        const point = data.points[0]; // Tıklanan nokta
        if(point?.data?.originalName === StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV) {

        }
    };

    return (
        <>
            <div>
                <div style={{display: 'flex', alignItems: 'end', margin: 8, marginRight: 50, marginLeft: 80}}>
                    {
                        globalVariables?.tar_plan_title && (
                            <Radio.Group
                                options={[
                                    {label: 'Base', value: 'Base'},
                                    {label: 'Both', value: 'Both'},
                                    {label: 'Comparison', value: 'Comparison'},
                                ]}
                                onChange={(e) => {
                                    setRadio(e.target.value)
                                }}
                                value={radio}
                                optionType="button"
                                buttonStyle="solid"
                                size={'small'}
                            />
                        )
                    }
                </div>
                <Resizable height={layoutSize.height} width={layoutSize.width} onResize={(event, {node, size, handle}) => {setLayoutSize({width: layoutSize.width, height: size.height})}}>
                    <div className="box" style={{width: layoutSize.width + 'px', height: layoutSize.height + 'px'}}>
                        <div className={'panel sidebar'} id={'plot-panel'} ref={plotImageRef} key={reset.toString()}>
                            <Plot
                                onClick={handleClick}
                                ref={plotRef}
                                data={plot.data}
                                onLegendClick={(e) => {
                                    const item = e.data[e.curveNumber];
                                    userConfig.current = {
                                        ...userConfig.current,
                                        [item?.originalName]: item?.visible === 'legendonly' ? true : 'legendonly'
                                    }

                                    if(e.data[e.curveNumber]?.originalName === 'B: Culvert') {
                                        e?.data?.forEach(x => {
                                            if(x.originalName?.includes('B: Culvert')) {
                                                x.visible = x?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }
                                    if(e.data[e.curveNumber]?.originalName === 'C: Culvert') {
                                        e?.data?.forEach(x => {
                                            if(x.originalName?.includes('C: Culvert')) {
                                                x.visible = x?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }

                                    if(e.data[e.curveNumber]?.originalName === 'B: Piers') {
                                        e?.data?.forEach(x => {
                                            if(x.originalName?.includes('B: Piers')) {
                                                x.visible = x?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }
                                    if(e.data[e.curveNumber]?.originalName === 'B: Abutments') {
                                        e?.data?.forEach(x => {
                                            if(x.originalName?.includes('B: Abutments')) {
                                                x.visible = x?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }
                                    if(e.data[e.curveNumber]?.originalName === 'C: Piers') {
                                        e?.data?.forEach(x => {
                                            if(x.originalName?.includes('C: Piers')) {
                                                x.visible = x?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }
                                    if(e.data[e.curveNumber]?.originalName === 'C: Abutments') {
                                        e?.data?.forEach(x => {
                                            if(x.originalName?.includes('C: Abutments')) {
                                                x.visible = x?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }

                                    if(e.data[e.curveNumber]?.originalName === StaElevPlotLayer.StaElevLayerNames.B_XS_INEFF) {
                                        e?.data?.forEach(x => {
                                            if(x.originalName?.includes('B: XS HATCHING') || x.originalName === 'B: Ineff Vertical') {
                                                x.visible = e.data[e.curveNumber]?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }
                                    if(e.data[e.curveNumber]?.originalName === StaElevPlotLayer.StaElevLayerNames.C_XS_INEFF) {
                                        e?.data?.forEach(x => {
                                            if(x.originalName?.includes('C: XS HATCHING') || x.originalName === 'C: Ineff Vertical') {
                                                x.visible = e.data[e.curveNumber]?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }

                                    if(e.data[e.curveNumber]?.originalName?.includes('B: WSEL')) {
                                        const hatchName = e.data[e.curveNumber]?.originalName?.replace('WSEL', 'XS HATCHING')
                                        e?.data?.forEach(x => {
                                            if(x.originalName === hatchName) {
                                                x.visible = e.data[e.curveNumber]?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }
                                    if(e.data[e.curveNumber]?.originalName?.includes('C: WSEL')) {
                                        const hatchName = e.data[e.curveNumber]?.originalName?.replace('WSEL', 'XS HATCHING')
                                        e?.data?.forEach(x => {
                                            if(x.originalName === hatchName) {
                                                x.visible = e.data[e.curveNumber]?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }

                                    if(e.data[e.curveNumber]?.originalName === StaElevPlotLayer.StaElevLayerNames.C_MANN) {
                                        e?.data?.forEach(x => {
                                            if(x.originalName === 'C: MANN_TEXTS') {
                                                x.visible = e.data[e.curveNumber]?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }
                                    if(e.data[e.curveNumber]?.originalName === StaElevPlotLayer.StaElevLayerNames.B_MANN) {
                                        e?.data?.forEach(x => {
                                            if(x.originalName === 'B: MANN_TEXTS') {
                                                x.visible = e.data[e.curveNumber]?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }

                                    // Toggle Ground Fill when Ground is toggled
                                    if(e.data[e.curveNumber]?.originalName === StaElevPlotLayer.StaElevLayerNames.B_STA_ELEV) {
                                        e?.data?.forEach(x => {
                                            if(x.originalName === 'B: Ground Fill') {
                                                x.visible = e.data[e.curveNumber]?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }
                                    if(e.data[e.curveNumber]?.originalName === StaElevPlotLayer.StaElevLayerNames.C_STA_ELEV) {
                                        e?.data?.forEach(x => {
                                            if(x.originalName === 'C: Ground Fill') {
                                                x.visible = e.data[e.curveNumber]?.visible === 'legendonly' ? true : 'legendonly'
                                            }
                                        })
                                    }

                                    return e
                                }}
                                config={{
                                    displayModeBar: true,
                                    displaylogo: false,
                                    scrollZoom: true,
                                    modeBarButtonsToReplace: [
                                        ['resetScale2d', {icon: undo}]
                                    ],
                                    modeBarButtonsToRemove: ['lasso2d', 'select', 'zoomin', 'zoomout', 'toImage', 'resetScale2d'],
                                    modeBarButtonsToAdd: [
                                        {
                                            name: 'Copy to Clipboard',
                                            icon: {
                                                width: 400,
                                                height: 500,
                                                path: 'M208 0H332.1c12.7 0 24.9 5.1 33.9 14.1l67.9 67.9c9 9 14.1 21.2 14.1 33.9V336c0 26.5-21.5 48-48 48H208c-26.5 0-48-21.5-48-48V48c0-26.5 21.5-48 48-48zM48 128h80v64H64V448H256V416h64v48c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V176c0-26.5 21.5-48 48-48z',
                                            },
                                            click: async function(gd) {
                                                Array.from(document.getElementsByClassName('modebar')).forEach(x => {
                                                    if(x) {
                                                        x.style.display = 'none';
                                                    }
                                                })
                                                const image = await takeScreenShot(plotImageRef.current)
                                                await navigator.clipboard.write([
                                                    new window.ClipboardItem({ "image/png": convertBase64ToBlob(image) }),
                                                ]);
                                                notification.success({
                                                    message: 'Image is copied to clipboard!'
                                                })
                                                Array.from(document.getElementsByClassName('modebar')).forEach(x => {
                                                    if(x) {
                                                        x.style.display = '';
                                                    }
                                                })
                                            }
                                        },
                                        // {
                                        //     name: 'Reset axes',
                                        //     icon: undo,
                                        //     click: function(gd) {
                                        //
                                        //     }
                                        // }
                                    ]
                                }}
                                layout={ {
                                    width: layoutSize.width,
                                    height: layoutSize.height,
                                    font: {
                                        family: 'Segoe ui',
                                        size: 16,
                                        color: '#000'
                                    },
                                    title: {
                                        text: plot.layout.title,
                                        font: {
                                            family: 'Segoe ui',
                                            size: 18,
                                            color: '#000'
                                        }
                                    },
                                    xaxis: plot.xaxis,
                                    yaxis: plot.yaxis,
                                    shape: {
                                        layer: 'above'
                                    },
                                    legend: {
                                        "orientation": "h",
                                        traceorder: 'normal',
                                        x:0.01,
                                        y: 1.05,
                                        font: {
                                            family: 'Segoe ui',
                                            size: 16,
                                            color: '#000'
                                        },
                                        bgcolor: '#fff',
                                        borderwidth: 0,
                                        title: globalVariables?.tar_plan_title ? {text: `<span style="font-family: 'Segoe UI';font-size: 16px"><b>Base</b> (${globalVariables?.sel_plan_title})</span>`} : '',
                                        groupclick: 'toggleitem'
                                    },
                                    legend2:{
                                        "orientation": "h",
                                        traceorder: 'normal',
                                        x:0.01,
                                        y: 0.94,
                                        font: {
                                            family: 'Segoe ui',
                                            size: 16,
                                            color: '#000'
                                        },
                                        bgcolor: '#fff',
                                        borderwidth: 0,
                                        // title: globalVariables?.tar_plan_title ? {text: `C: <b>${globalVariables?.tar_plan_title}</b>`} : '',
                                        title: {text: `<span style="font-family: 'Segoe UI';font-size: 16px"><b>Comparison</b> (${globalVariables?.tar_plan_title})</span>`},
                                        groupclick: 'toggleitem'
                                    },
                                } }
                            />
                        </div>
                    </div>
                </Resizable>
                <div style={{marginLeft: 8}}>
                    {
                        plot?.footnotes?.map(x => (
                            <ResultText>
                                {x}
                            </ResultText>
                        ))
                    }
                    <EmptyLine />
                </div>
            </div>
        </>
    )
}